import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { CalendarClock, X, Send } from "lucide-react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { createPortal } from "react-dom";

const TEAL = "#0D9488";
const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/** Client dialog to request a booking on a coach.
 *  Loads the coach's published slots, lets user pick a slot + upcoming date.
 */
export default function BookingRequestDialog({ open, onClose, coachId, coachLabel }) {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [date, setDate] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !coachId) return;
    setLoading(true);
    setSelected(null); setDate(""); setNote("");
    api.get(`/coaches/${coachId}/booking-slots`)
      .then(({ data }) => setSlots(Array.isArray(data) ? data : []))
      .catch(() => setSlots([]))
      .finally(() => setLoading(false));
  }, [open, coachId]);

  const upcomingDates = useMemo(() => {
    if (!selected) return [];
    const today = new Date();
    const target = selected.day_of_week; // 0=Mon
    const jsToday = (today.getDay() + 6) % 7; // JS Sun=0 → Mon=0
    const daysAhead = (target - jsToday + 7) % 7 || 7;
    const out = [];
    for (let i = 0; i < 4; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() + daysAhead + i * 7);
      out.push(d.toISOString().slice(0, 10));
    }
    return out;
  }, [selected]);

  useEffect(() => {
    if (upcomingDates.length && !upcomingDates.includes(date)) setDate(upcomingDates[0]);
  }, [upcomingDates, date]);

  if (!open) return null;

  const submit = async () => {
    if (!selected || !date) return;
    setBusy(true);
    try {
      await api.post("/coach/booking-requests", {
        coach_id: coachId,
        slot_id: selected.id,
        requested_date: date,
        note: note.trim() || null,
      });
      toast.success("Booking request sent");
      onClose?.(true);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return createPortal(
    <div className="fixed inset-0 z-[9990] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose} data-testid="booking-request-overlay">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
           onClick={(e) => e.stopPropagation()} data-testid="booking-request-dialog">
        <div className="px-5 py-4 flex items-center justify-between border-b border-slate-100">
          <div>
            <div className="text-[11px] uppercase tracking-widest font-semibold text-[#0D9488]">Book a check-in</div>
            <h3 className="font-display font-bold text-lg text-slate-900 mt-0.5">
              With {coachLabel || "your coach"}
            </h3>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700"
                  data-testid="booking-request-close" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 max-h-[70vh] overflow-y-auto">
          {loading ? (
            <div className="h-32 bg-slate-50 rounded animate-pulse" />
          ) : slots.length === 0 ? (
            <div className="text-sm text-slate-600 py-4 text-center" data-testid="booking-request-empty">
              This coach hasn't published any booking slots yet.
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-2">Pick a slot</div>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="booking-request-slots">
                  {slots.map((s) => {
                    const active = selected?.id === s.id;
                    return (
                      <li key={s.id}>
                        <button
                          type="button"
                          onClick={() => setSelected(s)}
                          className={`w-full text-left px-3 py-2 border rounded-lg transition-colors ${
                            active ? "border-[#0D9488] bg-teal-50" : "border-slate-200 hover:border-teal-300 hover:bg-slate-50"
                          }`}
                          data-testid={`booking-slot-${s.id}`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] uppercase tracking-widest font-bold text-teal-700 bg-teal-50 border border-teal-100 rounded px-1.5 py-0.5">{DAY_LABELS[s.day_of_week]}</span>
                            <span className="text-sm font-semibold text-slate-900">{s.start_time} → {s.end_time}</span>
                          </div>
                          {s.label && <div className="text-[11px] text-slate-500 mt-0.5">{s.label}</div>}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>

              {selected && (
                <div>
                  <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-2">Pick a date</div>
                  <div className="flex flex-wrap gap-1.5" data-testid="booking-request-dates">
                    {upcomingDates.map((d) => (
                      <button
                        key={d}
                        type="button"
                        onClick={() => setDate(d)}
                        className={`px-2.5 py-1.5 rounded-md text-[12px] font-mono-num border transition-colors ${
                          date === d
                            ? "bg-[#0D9488] text-white border-[#0D9488]"
                            : "bg-white text-slate-700 border-slate-200 hover:border-teal-300"
                        }`}
                        data-testid={`booking-date-${d}`}
                      >
                        {new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-1">Note to coach (optional)</div>
                <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value.slice(0, 280))}
                          placeholder="What would you like to cover?"
                          className="bg-white border-slate-200 focus-visible:ring-0 focus-visible:border-[#0D9488] text-sm"
                          data-testid="booking-request-note" />
                <div className="text-[10px] text-slate-400 font-mono-num mt-1">{note.length}/280</div>
              </div>
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-100 bg-slate-50/60 flex items-center justify-end gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={onClose}
                  className="text-slate-700 hover:bg-slate-100" data-testid="booking-request-cancel">
            Cancel
          </Button>
          <Button type="button" size="sm" onClick={submit}
                  disabled={!selected || !date || busy}
                  className="text-white" style={{ background: TEAL }}
                  data-testid="booking-request-submit">
            <Send className="w-3.5 h-3.5 mr-1" /> {busy ? "Sending…" : "Request booking"}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}
